import { useEffect, useRef, useState, useCallback } from 'react';
import styles from './index.module.scss';

interface ImageCache {
    [key: number]: HTMLImageElement;
}

const VideoFrame: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [firstImageLoaded, setFirstImageLoaded] = useState(false);
    const imageCache = useRef<ImageCache>({});
    const currentFrame = useRef(0);

    // 图片配置
    const TOTAL_FRAMES = 95;
    const BASE_URL = 'https://websiteoss.ecoflow.com/figma-uploads/';

    // 生成图片URL
    const getImageUrl = useCallback((frameNumber: number) => {
        if (window.innerWidth < 768) {
            // https://websiteoss.ecoflow.com/figma-uploads/m-video-frame-1.webp
            return `${BASE_URL}m-video-frame-${frameNumber}.webp`;
        }
        return `${BASE_URL}video-frame-${frameNumber}.webp`;
    }, []);

    // Canvas绘制函数 - 实现cover效果
    const drawFrame = useCallback((frameNumber: number) => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const image = imageCache.current[frameNumber];

        if (!canvas || !container || !image) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 获取容器的显示尺寸（不考虑DPR）
        const rect = container.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = rect.height;

        // 获取图片尺寸
        const imgWidth = image.naturalWidth;
        const imgHeight = image.naturalHeight;

        // 计算cover效果的缩放比例（基于显示尺寸）
        const displayRatio = displayWidth / displayHeight;
        const imgRatio = imgWidth / imgHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgRatio > displayRatio) {
            // 图片更宽，以高度为准
            drawHeight = displayHeight;
            drawWidth = imgWidth * (displayHeight / imgHeight);
            offsetX = (displayWidth - drawWidth) / 2;
            offsetY = 0;
        } else {
            // 图片更高，以宽度为准
            drawWidth = displayWidth;
            drawHeight = imgHeight * (displayWidth / imgWidth);
            offsetX = 0;
            offsetY = (displayHeight - drawHeight) / 2;
        }

        // 清除canvas并绘制图片
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

        currentFrame.current = frameNumber;
    }, []);

    // 优先加载第一张图片
    const loadFirstImage = useCallback(async () => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            imageCache.current[1] = img;
            setFirstImageLoaded(true);
            // 立即显示第一张图片
            setTimeout(() => {
                drawFrame(1);
            }, 100);
        };
        img.onerror = () => {
            console.warn('Failed to load first frame');
        };
        img.src = getImageUrl(1);
    }, [getImageUrl, drawFrame]);

    // 预加载所有图片
    const preloadImages = useCallback(async () => {
        console.log('开始预加载图片...');

        // 如果第一张图片还没加载，先加载它
        if (!firstImageLoaded) {
            await loadFirstImage();
        }

        const loadPromises: Promise<void>[] = [];

        for (let i = firstImageLoaded ? 2 : 1; i <= TOTAL_FRAMES; i++) {
            const promise = new Promise<void>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    imageCache.current[i] = img;
                    resolve();
                };
                img.onerror = () => {
                    console.warn(`Failed to load frame ${i}`);
                    reject(new Error(`Failed to load frame ${i}`));
                };
                img.src = getImageUrl(i);
            });
            loadPromises.push(promise);
        }

        try {
            await Promise.all(loadPromises);
            setImagesLoaded(true);
            console.log('所有图片预加载完成');
        } catch (error) {
            console.error('Error loading frames:', error);
            // 即使有部分图片加载失败，也继续执行
            setImagesLoaded(true);
        }
    }, [getImageUrl, firstImageLoaded, loadFirstImage, TOTAL_FRAMES]);

    // 调整canvas尺寸
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;

        if (!canvas || !container) return;

        // 获取容器尺寸
        const rect = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        // 设置canvas实际尺寸
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;

        // 设置canvas显示尺寸
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        // 缩放上下文以匹配设备像素比
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.scale(dpr, dpr);
        }

        // 重新绘制当前帧
        if (firstImageLoaded && currentFrame.current > 0) {
            drawFrame(currentFrame.current);
        } else if (firstImageLoaded) {
            // 如果没有当前帧，显示第一帧
            drawFrame(1);
        }
    }, [firstImageLoaded, drawFrame]);

    // 初始化和滚动控制
    useEffect(() => {
        if (typeof window === 'undefined' || !window.AniContainer) return;

        const viewPortHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        let sceneA: any = null;
        setTimeout(() => {
            sceneA = window.AniContainer.create({
                triggerElement: `#VideoFrame`,
                triggerHook: 0.5, // 进入视窗时触发
                offset: 0,
                duration: viewPortHeight * 1.5, // 滚动1.5个视窗高度的距离
                onProcess: (progress: number) => {
                    if (!firstImageLoaded) return;

                    // progress: 0~1，根据滚动进度控制视频帧
                    const frameNumber = Math.max(1, Math.min(TOTAL_FRAMES, Math.ceil(progress * TOTAL_FRAMES)));
                    console.log('progress', progress);
                    console.log('frameNumber', frameNumber);
                    if (frameNumber !== currentFrame.current) {
                        // 如果图片还没加载，显示第一张
                        if (!imageCache.current[frameNumber] && firstImageLoaded) {
                            drawFrame(1);
                        } else if (imageCache.current[frameNumber]) {
                            drawFrame(frameNumber);
                        }
                    }
                },
                onEnter: () => {
                    // 滚动到组件时，如果图片还没开始加载，立即开始
                    if (!firstImageLoaded) {
                        loadFirstImage();
                    }
                    // 如果预加载还没开始，立即开始
                    if (!imagesLoaded && Object.keys(imageCache.current).length <= 1) {
                        preloadImages();
                    }
                },
            });
        }, 100);

        return () => {
            // 清理滚动监听
            if (sceneA && typeof sceneA.destroy === 'function') {
                sceneA.destroy();
            }
        };
    }, [firstImageLoaded, imagesLoaded, drawFrame, loadFirstImage, preloadImages]);

    // 窗口尺寸变化监听
    useEffect(() => {
        resizeCanvas();

        const handleResize = () => {
            resizeCanvas();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [resizeCanvas]);

    // 页面加载完毕后立即开始预加载
    useEffect(() => {
        const initializeComponent = () => {
            resizeCanvas();
            // 立即开始加载第一张图片
            if (!firstImageLoaded) {
                loadFirstImage();
            }
            // 延迟一点时间后开始预加载所有图片，避免阻塞首屏渲染
            setTimeout(() => {
                if (!imagesLoaded && Object.keys(imageCache.current).length <= 1) {
                    preloadImages();
                }
            }, 1000); // 1秒后开始预加载，确保首屏内容已稳定
        };

        const timer = setTimeout(initializeComponent, 100); // 延迟确保DOM已渲染
        return () => clearTimeout(timer);
    }, [resizeCanvas, firstImageLoaded, loadFirstImage, imagesLoaded, preloadImages]);

    return (
        <div className={styles.VideoFrame} id="VideoFrame" ref={containerRef}>
            <canvas ref={canvasRef} className={styles.canvas} />
        </div>
    );
};

export default VideoFrame;
